import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal, { Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";
import { toast } from "sonner";

const FAQS = [
  {
    id: "buy",
    q: "Where can I buy the journal?",
    a: "Right now! The $9.99 digital fillable & printable PDF is available on Etsy as an instant download, and the 30-page printed journal is $15.59 on Amazon. You'll find both on the Shop page.",
  },
  {
    id: "advice",
    q: "Is this medical advice?",
    a: "No. In Your Corner Healthcare is a healthcare organization and advocacy tool. It complements MyChart, patient portals, medical records, and your care team—it never replaces your doctor or professional medical advice.",
  },
  {
    id: "who",
    q: "Who is it for?",
    a: "Older adults, caregivers, patients juggling multiple providers or conditions, people who prefer paper, and anyone who finds patient portals hard to navigate. If you have healthcare to keep track of, it's for you.",
  },
  {
    id: "difference",
    q: "What's the difference between the digital and printed journal?",
    a: "The digital edition is a 14-page fillable and printable PDF, available for instant download. The physical edition is a 30-page professionally printed journal, available through Amazon.",
  },
];

const TOPICS = [
  "General question",
  "A question about a product",
  "Just saying hi",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent—thank you. We'll get back to you soon.", {
        "data-testid": "contact-success-toast",
      });
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-ink/10 bg-cream px-5 py-4 text-sm text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-lav focus:ring-2 focus:ring-lav/25";

  return (
    <main data-testid="page-contact" className="pt-32 sm:pt-40 lg:pt-44">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Say hello</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-7 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="contact-headline">
              Ask anything. We're in your corner.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft sm:text-lg" data-testid="contact-sub">
              Questions about the journal, the upcoming collection, or just want to say hi? Every message goes
              straight to us.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <Reveal>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-ink/5 bg-white p-7 shadow-xl shadow-lav-soft/50 sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink-soft">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={set("name")}
                    required
                    maxLength={120}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink-soft">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={set("email")}
                    required
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-topic" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink-soft">
                  Topic
                </label>
                <select
                  id="contact-topic"
                  data-testid="contact-input-topic"
                  value={form.topic}
                  onChange={set("topic")}
                  className={`${inputCls} appearance-none`}
                >
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block text-xs font-bold uppercase tracking-[0.16em] text-ink-soft">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  required
                  rows={6}
                  maxLength={5000}
                  placeholder="What's on your mind?"
                  className={`${inputCls} resize-y`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-bold text-cream transition-all duration-300 hover:bg-lav-deep disabled:cursor-wait disabled:opacity-70 sm:w-auto"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>

          <div>
            <Reveal delay={0.08}>
              <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl" data-testid="faq-headline">
                Quick answers
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <Accordion type="single" collapsible className="mt-6">
                {FAQS.map((f) => (
                  <AccordionItem key={f.id} value={f.id} data-testid={`faq-item-${f.id}`}>
                    <AccordionTrigger className="text-left font-display text-lg font-semibold text-ink">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-ink-soft sm:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-8 rounded-3xl bg-mint-soft p-7">
                <p className="text-sm leading-relaxed text-ink-soft" data-testid="contact-care-note">
                  In Your Corner Healthcare provides healthcare organization tools and educational resources. We
                  cannot provide medical advice, diagnosis, or treatment. For questions about your health or
                  medical care, please contact a qualified healthcare professional.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
